import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { execApi } from '../../../services/api';
import { TherapistCompEditor } from './TherapistCompEditor';
import { BonusPolicyEditor } from './BonusPolicyEditor';
import { BonusValues } from './BonusFields';
import { TherapistDetail, DetailTherapist } from '../ExecSalary/TherapistDetail';
import { ColumnFilter } from '../ExecSalary/SalaryFilters';
import { firstName } from '../ExecSalary/firstName';
import { thisMonth, prevMonth } from '../../../utils/date';

// Stage 3 — the accounting area (Lee & Tal). Columns mirror Lee's Excel: full name,
// role, שכר-ללא-מע"מ, כולל-מע"מ, ניכוי-מס-במקור, נטו-לתשלום, מס'-חשבונית + its type,
// אישור-לילך, אישור-לי. Sorted by FIRST name. Approved rows sink to the bottom.
// The עובד and שולם columns were dropped at Lee's request (2026-07-27); the paid FLAG
// is still stored and still travels in snapshots, it simply has no column.

const ils = (n: number) => '₪' + (n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Click a money figure to copy it (Lee's ask, 2026-09-14): she retypes these numbers
// into the invoice system, so what lands on the clipboard is the PLAIN number —
// 4231.50, no ₪ and no thousands separator — while the cell still reads ₪4,231.50.
// The display is untouched; only a hover tint and the cursor say it is clickable.
function CopyAmount({ value, children, className = '' }: { value: number; children: ReactNode; className?: string }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  const copy = async () => {
    const raw = (value ?? 0).toFixed(2);
    try {
      await navigator.clipboard.writeText(raw);
    } catch {
      // navigator.clipboard needs a secure context; keep the old selection trick
      // as a fallback so this never silently does nothing.
      const ta = document.createElement('textarea');
      ta.value = raw;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } finally { document.body.removeChild(ta); }
    }
    setCopied(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), 1200);
  };

  return (
    <button
      type="button"
      onClick={copy}
      title="לחצו להעתקת הסכום"
      className={`relative rounded px-1 -mx-1 tabular-nums transition-colors hover:bg-blue-50 focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-400 ${className}`}
    >
      {children}
      {copied && (
        <span className="pointer-events-none absolute -top-5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-gray-800 px-1.5 py-0.5 text-[10px] font-normal text-white shadow">
          הועתק
        </span>
      )}
    </button>
  );
}

interface Billing {
  employment: string; vat_registered: boolean; withholding_rate: number;
  invoice_type?: string;           // tax = חשבונית מס | deal = חשבונית עסקה
  incl_vat: number; withholding_amount: number; net_pay: number;
  // false = nobody filled the profile; every figure here is a backend DEFAULT
  // (freelancer, VAT, 0% withholding) — the screen must not present it as real.
  is_set?: boolean;
  email?: string | null;           // set here, overrides the clinical system
  email_default?: string | null;   // the clinical system's address
}
interface Rates extends BonusValues {
  workedRate: number; nonClinicalRate: number; oncallRate: number;
  tariffIntakeAdult: number; tariffIntakeYouth: number;
  tariffFollowupAdult: number; tariffFollowupYouth: number;
  intakeLengthAdults: number; intakeLengthYouth: number;
  followupLengthAdults: number; followupLengthYouth: number;
  noshowPct: number;
  overridden?: string[]; from_global?: string[];
}
interface TherapistPay {
  name: string; hebrew_name: string | null; role: string;
  total_payment: number; billing: Billing; rates: Rates;
  completed_meetings: number; archived: boolean;
  // Synthetic test therapist — shown in the table, excluded from every sum.
  demo?: boolean;
  // On the on-call schedule — the editor shows the on-call tariff only then.
  oncall_doctor?: boolean;
}
interface SalaryMonth { summary: { month: string; unmatched_detail?: Unmatched[] }; therapists: TherapistPay[]; }

// A new employee starts with the bonus off and everything at zero, mirroring the
// backend's _new_roster_entry — a new hire should never inherit a wrong default.
// Zeroed bonus policy for a therapist being created. Exported so the payroll
// screen's "add" flow uses the same defaults instead of a second copy.
export const NEW_BONUS: BonusValues = {
  bonusEnabled: false, bonusNoshowThreshold: 0, bonusUtilizationThreshold: 0,
  bonusTier1MinHours: 0, bonusTier1Pct: 0, bonusTier2MinHours: 0, bonusTier2Pct: 0,
  bonusTier3MinHours: 0, bonusTier3Pct: 0,
};

const MONTHS = 12;

const INVOICE_LABEL: Record<string, string> = { tax: 'חשבונית מס', deal: 'חשבונית עסקה' };

// How this provider invoices us — a standing property of theirs, so it is saved on the
// billing profile rather than per month. One click flips it: the cell updates locally
// (nothing else on the row depends on it) and the profile is saved in the background.
function InvoiceTypeToggle({ t, month, onFlip }: { t: TherapistPay; month: string; onFlip: (v: string) => void }) {
  const [busy, setBusy] = useState(false);
  const cur = t.billing.invoice_type || 'tax';
  const next = cur === 'tax' ? 'deal' : 'tax';
  const flip = async () => {
    setBusy(true);
    onFlip(next);
    try {
      await execApi.saveSalaryProfile({
        therapist: t.name,
        employment: t.billing.employment,
        vat_registered: t.billing.vat_registered,
        withholding_rate: t.billing.withholding_rate,
        invoice_type: next,
        month,
      });
    } catch {
      onFlip(cur);           // save failed — put the cell back so it never lies
    } finally { setBusy(false); }
  };
  return (
    <button onClick={flip} disabled={busy}
      title={`לחיצה תחליף ל${INVOICE_LABEL[next]}`}
      className={`text-[11px] rounded px-1.5 py-1 border whitespace-nowrap ${cur === 'tax'
        ? 'bg-gray-50 text-gray-600 border-gray-200'
        : 'bg-violet-50 text-violet-700 border-violet-200'}`}>
      {cur === 'tax' ? 'מס' : 'עסקה'}
    </button>
  );
}

// The four per-meeting tariffs. A therapist with none of them AND no hourly rate is
// paid nothing, silently — see `noTariff` below.
const TARIFF_FIELDS: (keyof Rates)[] = ['tariffIntakeAdult', 'tariffIntakeYouth',
  'tariffFollowupAdult', 'tariffFollowupYouth'];

interface Unmatched {
  name: string; completed_meetings: number;
  suggested_type: string; psy_meetings: number; cm_meetings: number;
}

export function ExecBilling() {
  const [month, setMonth] = useState<string>(prevMonth());
  const [data, setData] = useState<SalaryMonth | null>(null);
  const [loading, setLoading] = useState(true);
  // A refresh after an edit must not blank the table — the numbers are still valid
  // while the new ones are on their way.
  const [refreshing, setRefreshing] = useState(false);
  // A save that failed. Shown until the next successful load, because the flag it
  // was meant to set is not on the server and nothing else on screen says so.
  const [saveError, setSaveError] = useState('');
  const loadedOnce = useRef(false);
  const [q, setQ] = useState('');
  // Lee's month-by-month view for one person, fetched on demand: it costs about
  // what loading a month costs, so it is a click and not part of the page load.
  const [histFor, setHistFor] = useState<string | null>(null);
  const [hist, setHist] = useState<any | null>(null);
  const [histLoading, setHistLoading] = useState(false);

  // Lee gets two invoices at once covering two past months, and ticking them meant
  // switching month, finding the person, ticking, switching back. The same endpoint
  // takes a month, so the history can write straight into it.
  const tickHistory = async (name: string, m: string, field: string, next: boolean) => {
    setHist((h: any) => h && ({
      ...h,
      months: h.months.map((x: any) => x.month === m ? { ...x, [field]: next } : x),
    }));
    try {
      await execApi.setBillingStatus({ therapist: name, month: m, field, value: String(next) });
      // The month screen reads the same flags; refresh it if it is showing that month.
      if (m === month) load(month);
    } catch {
      // Put the tick back rather than leave the screen claiming something it failed
      // to save — the silent catch is what bit us on this screen before.
      setHist((h: any) => h && ({
        ...h,
        months: h.months.map((x: any) => x.month === m ? { ...x, [field]: !next } : x),
      }));
      setSaveError(`השמירה נכשלה (${name} · ${m}) — הסימון לא נשמר`);
    }
  };

  const openHistory = async (name: string) => {
    setHistFor(name); setHist(null); setHistLoading(true);
    try {
      const res = await execApi.getBillingHistory(name);
      setHist(res.data);
    } catch {
      setHist(null);
    } finally {
      setHistLoading(false);
    }
  };
  const [roleFilter, setRoleFilter] = useState<'all' | 'psy' | 'cm' | 'archived'>('all');
  // One filter per stage column (Ohad, 2026-08-04: "filter on every column") —
  // replaced the single combined approval dropdown.
  const [fBasel, setFBasel] = useState<'all' | 'yes' | 'no'>('all');
  const [fTal, setFTal] = useState<'all' | 'yes' | 'no'>('all');
  const [fLee, setFLee] = useState<'all' | 'yes' | 'no'>('all');
  const [fInvoice, setFInvoice] = useState<'all' | 'set' | 'empty'>('all');
  const [fInvType, setFInvType] = useState<'all' | 'tax' | 'deal'>('all');
  const [fProfile, setFProfile] = useState<'all' | 'set' | 'missing'>('all');
  const [editingT, setEditingT] = useState<TherapistPay | null>(null);
  const [detailT, setDetailT] = useState<TherapistPay | null>(null);
  const [addingNew, setAddingNew] = useState(false);
  // Opening the editor from the red banner carries the calendar's spelling with it:
  // the name IS the key the meetings attach to, so retyping it is how a doctor ends
  // up on the roster twice (Ohad, 2026-10-06).
  const [addingNewName, setAddingNewName] = useState<string | null>(null);
  const [addingNewType, setAddingNewType] = useState<string>('');
  const [editingBonus, setEditingBonus] = useState(false);
  const [snaps, setSnaps] = useState<{ snap_id: string; label: string; created_at: string }[]>([]);
  const [viewingSnap, setViewingSnap] = useState<string | null>(null);
  // Local (not-yet-persisted) accounting state, keyed by therapist name.
  const [deleted, setDeleted] = useState<string[]>([]);
  const [showDeleted, setShowDeleted] = useState(false);
  const [inv, setInv] = useState<Record<string, string>>({});
  const [tal, setTal] = useState<Record<string, boolean>>({});
  const [lee, setLee] = useState<Record<string, boolean>>({});
  // Read-only here. Basel signs off in the SALARY dashboard; this screen shows the
  // state and the backend refuses to set it from an accounting call (Ohad, 2026-07-30).
  const [basel, setBasel] = useState<Record<string, boolean>>({});
  // The therapist's own mark that an invoice is on its way, with the date. Read-only
  // here: accounting displays it, only the therapist sets it (Ohad, 2026-10-06).
  const [invSent, setInvSent] = useState<Record<string, string>>({});
  // Doctors the calendar knows and payroll does not. Their meetings are dropped from
  // every total, so the only honest place for them is the top of the screen.
  const [unmatched, setUnmatched] = useState<Unmatched[]>([]);
  const [paid, setPaid] = useState<Record<string, boolean>>({});

  // All four calls at once. They used to run one after another — salary (~3s) then
  // status, snapshots and deleted (~1.4s each, which is what a BigQuery round trip
  // costs even for zero rows) — so every save cost about seven seconds of waiting.
  // In parallel the wait is the slowest single call, and the three small ones are free.
  const load = async (m?: string) => {
    if (!loadedOnce.current) setLoading(true); else setRefreshing(true);
    try {
      // The three small calls need a month, and on the very first load the month is
      // whatever the server decides. Assuming the current month lets all four go at
      // once; if the server disagrees (a timezone edge at a month boundary) the three
      // are re-fetched for the month it actually returned.
      // 🔴 The PREVIOUS month, not this one (Ohad, 2026-10-28). This screen is where
      // a month is settled with the accountant, and that work happens after the month
      // ends: through October the live question is September. Opening on the current
      // month showed a half-finished period that nobody is working on yet, and made
      // the real one a click away every single time.
      // Only this screen moves. Basel's payroll screen keeps the server's default.
      const mm = m || prevMonth();
      const side = (mo: string) => Promise.all([
        execApi.getBillingStatus(mo),
        execApi.listBillingSnapshots(mo),
        execApi.getDeletedTherapists(mo),
      ]);
      const [salaryRes, sideRes] = await Promise.all([
        execApi.getSalary(mm, true),           // all roster, incl. zero-activity
        side(mm),
      ]);
      const sm = salaryRes.data as SalaryMonth;
      // Whoever did not work this month is not on this screen (Ohad, 02/11: "לא
      // צריך להופיע מי שלא עבד"). The whole roster is still fetched — someone with
      // no meetings but a payment (an on-call month, a manual line) stays — but a
      // row with nothing to pay is a row Lee has to read past; new hires appeared
      // in every month before they started, marked "חסר", and read as a sync bug.
      setData({
        ...sm,
        therapists: sm.therapists.filter((t) => t.completed_meetings > 0 || t.total_payment !== 0),
      });
      setUnmatched(sm.summary.unmatched_detail || []);
      if (!m) setMonth(sm.summary.month);
      const [statusRes, snapsRes, delRes] =
        sm.summary.month === mm ? sideRes : await side(sm.summary.month);
      const st = statusRes.data as Record<string, any>;
      const iv: Record<string, string> = {}, ta: Record<string, boolean> = {},
        le: Record<string, boolean> = {}, pa: Record<string, boolean> = {},
        ba: Record<string, boolean> = {}, isent: Record<string, string> = {};
      Object.entries(st || {}).forEach(([name, v]: [string, any]) => {
        if (v.invoice) iv[name] = v.invoice;
        if (v.tal) ta[name] = true;
        if (v.lee) le[name] = true;
        if (v.paid) pa[name] = true;
        if (v.basel) ba[name] = true;
        if (v.invoice_sent) isent[name] = v.invoice_sent_at || '';
      });
      setInv(iv); setTal(ta); setLee(le); setPaid(pa); setBasel(ba); setInvSent(isent);
      setViewingSnap(null);
      setSaveError('');       // the screen now matches the server again
      setSnaps((snapsRes.data as any[]) || []);
      setDeleted((delRes.data as string[]) || []);
      loadedOnce.current = true;
    } finally { setLoading(false); setRefreshing(false); }
  };

  useEffect(() => { load(); }, []);

  const saveSnap = async () => {
    const label = new Date().toLocaleString('he-IL');
    const payload = JSON.stringify({ therapists: data?.therapists || [], status: { inv, tal, lee, paid, basel, invSent } });
    await execApi.saveBillingSnapshot({ month, label, payload });
    setSnaps(((await execApi.listBillingSnapshots(month)).data as any[]) || []);
  };
  // Once Basel has signed off every real therapist, the month's figures stop moving —
  // and that is the moment worth keeping, not whenever someone remembers to press the
  // button. Fires once per month: the label carries a marker the check looks for, so
  // a reload, a re-render or a second tab cannot produce a second copy
  // (Ohad, 2026-10-06).
  const AUTO_SNAP = 'אוטומטי · כל האישורים הושלמו';
  const autoSnapped = useRef(false);
  useEffect(() => {
    if (viewingSnap || loading || refreshing) return;
    const real = (data?.therapists || []).filter((t) => !t.archived && !t.demo);
    if (real.length === 0) return;
    if (!real.every((t) => basel[t.name])) return;
    if (autoSnapped.current) return;
    if (snaps.some((sn) => sn.label.startsWith(AUTO_SNAP))) { autoSnapped.current = true; return; }
    autoSnapped.current = true;
    (async () => {
      const label = `${AUTO_SNAP} · ${new Date().toLocaleString('he-IL')}`;
      const payload = JSON.stringify({
        therapists: data?.therapists || [],
        status: { inv, tal, lee, paid, basel, invSent },
      });
      try {
        await execApi.saveBillingSnapshot({ month, label, payload });
        setSnaps(((await execApi.listBillingSnapshots(month)).data as any[]) || []);
      } catch {
        autoSnapped.current = false;   // a failed save must not look like a saved one
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, basel, snaps, viewingSnap, loading, refreshing, month]);

  // A different month is a different "all approved" question.
  useEffect(() => { autoSnapped.current = false; }, [month]);

  const loadSnap = async (snapId: string, label: string) => {
    const p = (await execApi.getBillingSnapshot(snapId)).data.payload;
    setData({ summary: { month }, therapists: p.therapists });
    setUnmatched([]);          // a snapshot is a frozen picture, not a live warning
    setInv(p.status?.inv || {}); setTal(p.status?.tal || {});
    setLee(p.status?.lee || {}); setPaid(p.status?.paid || {});
    setBasel(p.status?.basel || {});
    setInvSent(p.status?.invSent || {});
    setViewingSnap(label || snapId);
  };

  // Patch one therapist's invoice type in place — avoids refetching the whole month
  // for a one-field flip.
  const setInvoiceType = (therapist: string, invoice_type: string) =>
    setData((d) => d && {
      ...d,
      therapists: d.therapists.map((t) =>
        t.name === therapist ? { ...t, billing: { ...t.billing, invoice_type } } : t),
    });

  // Persist one accounting field. The checkbox already flipped locally, so a failure
  // has to be shown and undone: swallowing it (Lee, 2026-09-02) left a tick on screen
  // that was never saved, and the mark only vanished at the next reload — which reads
  // as "the two screens don't sync". Reloading the month puts every flag back to what
  // the server actually holds rather than guessing which one to revert.
  const persist = (therapist: string, field: string, value: string) => {
    if (viewingSnap) return;   // read-only while viewing a past snapshot
    execApi.setBillingStatus({ therapist, month, field, value }).catch(() => {
      setSaveError(`השמירה נכשלה (${therapist}) — הסימון לא נשמר`);
      load(month);
    });
  };

  // Export the CURRENT tab's rows as a CSV (Excel-openable, BOM for Hebrew).
  const exportCsv = () => {
    const head = ['שם מלא', 'תפקיד', 'תשלום ללא מע״מ', 'כולל מע״מ', 'ניכוי %', 'ניכוי ₪', 'נטו לתשלום', 'מס׳ חשבונית', 'סוג חשבונית', 'אישור באסל', 'אישור לילך', 'אישור לי'];
    const body = rows.map((t) => [
      t.hebrew_name || t.name,
      t.role === 'psy' ? 'פסיכיאטר' : 'מנהל תיק',
      Math.round(t.total_payment),
      t.billing.vat_registered ? Math.round(t.billing.incl_vat) : '',
      t.billing.withholding_rate > 0 ? Math.round(t.billing.withholding_rate * 100) + '%' : '',
      t.billing.withholding_amount ? Math.round(t.billing.withholding_amount) : '',
      Math.round(t.billing.net_pay),
      inv[t.name] || '',
      INVOICE_LABEL[t.billing.invoice_type || 'tax'],
      basel[t.name] ? 'תקין' : '',
      tal[t.name] ? 'תקין' : '',
      lee[t.name] ? 'תקין' : '',
    ]);
    const csv = '﻿' + [head, ...body]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `הנהלת-חשבונות-${month}-${roleFilter}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const all = useMemo(() => data?.therapists || [], [data]);
  const counts = useMemo(() => {
    // Demo therapists stay out of the headcount — they are fixtures, not staff.
    const active = all.filter((t) => !t.archived && !t.demo);
    return {
      all: active.length,
      psy: active.filter((t) => t.role === 'psy').length,
      cm: active.filter((t) => t.role === 'cm').length,
      archived: all.filter((t) => t.archived && !t.demo).length,
    };
  }, [all]);

  const rows = useMemo(() => {
    const pool = roleFilter === 'archived' ? all.filter((t) => t.archived) : all.filter((t) => !t.archived);
    // Where each row stands, column by column (Ohad, 2026-08-04) — a yes/no per
    // gate, invoice entered or not, invoice type, and profile filled or assumed.
    const ok = (t: TherapistPay) =>
      (fBasel === 'all' || (fBasel === 'yes') === !!basel[t.name]) &&
      (fTal === 'all' || (fTal === 'yes') === !!tal[t.name]) &&
      (fLee === 'all' || (fLee === 'yes') === !!lee[t.name]) &&
      (fInvoice === 'all' || (fInvoice === 'set') === !!(inv[t.name] || '').trim()) &&
      (fInvType === 'all' || (t.billing.invoice_type || 'tax') === fInvType) &&
      (fProfile === 'all' || (fProfile === 'set') === (t.billing.is_set !== false));
    const list = pool.filter((t) =>
      (roleFilter === 'all' || roleFilter === 'archived' || t.role === roleFilter) &&
      ok(t) &&
      (!q || (t.hebrew_name || '').includes(q) || t.name.toLowerCase().includes(q.toLowerCase())));
    return [...list].sort((a, b) => {
      // approved (both) sink to the bottom; otherwise alphabetical by first name.
      const aDone = tal[a.name] && lee[a.name] ? 1 : 0;
      const bDone = tal[b.name] && lee[b.name] ? 1 : 0;
      if (aDone !== bDone) return aDone - bDone;
      return firstName(a).localeCompare(firstName(b), 'he');
    });
  }, [all, q, roleFilter, fBasel, fTal, fLee, fInvoice, fInvType, fProfile, basel, tal, lee, inv]);

  // Counts per filter option, over the active (non-archived, real) roster.
  const fCounts = useMemo(() => {
    const base = all.filter((t) => !t.archived && !t.demo);
    const n = (f: (t: TherapistPay) => boolean) => base.filter(f).length;
    return {
      all: base.length,
      basel: n((t) => !!basel[t.name]),
      tal: n((t) => !!tal[t.name]),
      lee: n((t) => !!lee[t.name]),
      invoice: n((t) => !!(inv[t.name] || '').trim()),
      tax: n((t) => (t.billing.invoice_type || 'tax') === 'tax'),
      deal: n((t) => (t.billing.invoice_type || 'tax') === 'deal'),
      profile: n((t) => t.billing.is_set !== false),
    };
  }, [all, basel, tal, lee, inv]);

  // Bottom line. "נותר לתשלום" counts a row as still owed until it carries BOTH
  // approvals (Ohad 2026-07-27: one tick alone is not paid) — and it is the NET, the
  // figure that actually leaves the bank. Sums whatever the current tab/search shows.
  const totals = useMemo(() => {
    // Money totals count REAL therapists only — a demo row's shekels are fake.
    const real = rows.filter((t) => !t.demo);
    const pending = real.filter((t) => !(tal[t.name] && lee[t.name]));
    const sum = (list: TherapistPay[], f: (t: TherapistPay) => number) =>
      list.reduce((s, t) => s + (f(t) || 0), 0);
    const done = real.filter((t) => tal[t.name] && lee[t.name]);
    return {
      before_vat: sum(real, (t) => t.total_payment),
      incl_vat: sum(real, (t) => t.billing.incl_vat),
      withholding: sum(real, (t) => t.billing.withholding_amount),
      net: sum(real, (t) => t.billing.net_pay),
      remaining: sum(pending, (t) => t.billing.net_pay),
      pending: pending.length,
      paid: sum(done, (t) => t.billing.net_pay),
      paidCount: done.length,
    };
  }, [rows, tal, lee]);

  // The silent gap, made loud: therapists whose billing profile was never filled.
  // Their net on this screen is computed from assumed defaults — Lee's to-do queue.
  const missingProfiles = useMemo(
    () => all.filter((t) => !t.archived && !t.demo && t.billing.is_set === false),
    [all],
  );
  const missingGross = useMemo(
    () => missingProfiles.reduce((s, t) => s + (t.total_payment || 0), 0),
    [missingProfiles],
  );

  // Worked and got nothing: meetings were held, but no per-meeting tariff and no hourly
  // rate exist, so exec_salary._done_amount falls through to 0/60 x 0 and pays ₪0.00 —
  // on screen indistinguishable from a therapist who simply did not work. Rami Khouri
  // (47 meetings) and Vered Levy sat like that (Ohad, 2026-10-06).
  const noTariff = useMemo(
    () => all.filter((t) => !t.archived && !t.demo && (t.completed_meetings || 0) > 0
      && !(Number(t.rates.workedRate) > 0)
      && !TARIFF_FIELDS.some((f) => Number(t.rates[f]) > 0)),
    [all],
  );
  const noTariffMeetings = useMemo(
    () => noTariff.reduce((s, t) => s + (t.completed_meetings || 0), 0),
    [noTariff],
  );

  // Anchored on TODAY, not on the month the screen happened to open with. It used to
  // count backwards from the server's "last open month", so any month after that one
  // — including the current one — simply had no entry, and there was nowhere to set a
  // therapist's tariff for it. The payroll screen always offered those months, so the
  // two screens disagreed about which months exist (Ohad, 2026-08-30).
  const monthOptions = useMemo(() => {
    const now = new Date();
    const list = Array.from({ length: MONTHS }, (_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    });
    // A month restored from further back stays reachable rather than vanishing.
    return month && !list.includes(month) ? [...list, month].sort().reverse() : list;
  }, [month]);

  const TABS: { key: 'all' | 'psy' | 'cm' | 'archived'; label: string }[] = [
    { key: 'all', label: 'כולם' },
    { key: 'psy', label: 'רופאים' },
    { key: 'cm', label: 'מנהלי תיק' },
    { key: 'archived', label: 'ארכיון' },
  ];

  return (
    <div dir="rtl">
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <select value={month} onChange={(e) => { setMonth(e.target.value); load(e.target.value); }}
          className="text-sm border border-gray-300 rounded px-3 py-1.5 bg-white">
          {monthOptions.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <input value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="חיפוש מטפל לאישור…"
          className="text-sm border border-gray-300 rounded px-3 py-1.5 w-64" />
        <ColumnFilter title="באסל" value={fBasel} onChange={setFBasel}
          options={[
            { v: 'all', label: 'הכל', count: fCounts.all },
            { v: 'yes', label: 'אושר', count: fCounts.basel },
            { v: 'no', label: 'ממתין', count: fCounts.all - fCounts.basel },
          ]} />
        <ColumnFilter title="לילך" value={fTal} onChange={setFTal}
          options={[
            { v: 'all', label: 'הכל', count: fCounts.all },
            { v: 'yes', label: 'אושר', count: fCounts.tal },
            { v: 'no', label: 'ממתין', count: fCounts.all - fCounts.tal },
          ]} />
        <ColumnFilter title="לי" value={fLee} onChange={setFLee}
          options={[
            { v: 'all', label: 'הכל', count: fCounts.all },
            { v: 'yes', label: 'אושר', count: fCounts.lee },
            { v: 'no', label: 'ממתין', count: fCounts.all - fCounts.lee },
          ]} />
        <ColumnFilter title="חשבונית" value={fInvoice} onChange={setFInvoice}
          options={[
            { v: 'all', label: 'הכל', count: fCounts.all },
            { v: 'set', label: 'הוזן מס׳', count: fCounts.invoice },
            { v: 'empty', label: 'חסר', count: fCounts.all - fCounts.invoice },
          ]} />
        <ColumnFilter title="סוג" value={fInvType} onChange={setFInvType}
          options={[
            { v: 'all', label: 'הכל', count: fCounts.all },
            { v: 'tax', label: 'חשבונית מס', count: fCounts.tax },
            { v: 'deal', label: 'חשבונית עסקה', count: fCounts.deal },
          ]} />
        <ColumnFilter title="פרופיל" value={fProfile} onChange={setFProfile}
          options={[
            { v: 'all', label: 'הכל', count: fCounts.all },
            { v: 'set', label: 'מוגדר', count: fCounts.profile },
            { v: 'missing', label: 'חסר', count: fCounts.all - fCounts.profile },
          ]} />
        {/* Basel works the payments screen at the same time, and neither screen hears
            about the other's saves — so there has to be a way to pull the current
            state without reloading the browser (Lee, 2026-09-02). */}
        <button onClick={() => load(month)} disabled={refreshing || !!viewingSnap}
          title="טעינה מחדש מהשרת — מציג שינויים שנעשו במסך התשלומים"
          className="text-sm rounded-lg px-3 py-1.5 border border-gray-200 bg-white text-gray-600 hover:border-gray-300 disabled:opacity-50">
          ↻ רענון
        </button>
        <span className="text-xs text-gray-500 mr-auto">
          {refreshing && <span className="text-blue-500 ml-2">מעדכן…</span>}
          מאושר יורד לתחתית · ממוין לפי שם פרטי · {rows.length} מטפלים
        </span>
      </div>

      {saveError && (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          ⚠ {saveError}
        </div>
      )}

      <div className="flex items-center gap-1 mb-4">
        {TABS.map((tb) => (
          <button key={tb.key} onClick={() => setRoleFilter(tb.key)}
            className={`text-sm rounded-lg px-4 py-1.5 border ${roleFilter === tb.key
              ? 'bg-indigo-600 text-white border-indigo-600'
              : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}>
            {tb.label} <span className={roleFilter === tb.key ? 'text-indigo-200' : 'text-gray-500'}>{counts[tb.key]}</span>
          </button>
        ))}
        <div className="mr-auto flex items-center gap-2">
          {snaps.length > 0 && (
            <select value="" onChange={(e) => { const s = snaps.find((x) => x.snap_id === e.target.value); if (s) loadSnap(s.snap_id, s.label); }}
              className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 bg-white text-gray-600">
              <option value="">snapshots ({snaps.length})…</option>
              {snaps.map((s) => <option key={s.snap_id} value={s.snap_id}>{s.label}</option>)}
            </select>
          )}
          <button onClick={saveSnap}
            className="inline-flex items-center gap-1 text-sm rounded-lg px-4 py-1.5 border border-gray-200 text-gray-600 hover:border-gray-300">
            📸 snapshot
          </button>
          <button onClick={exportCsv}
            className="inline-flex items-center gap-1 text-sm rounded-lg px-4 py-1.5 border border-gray-200 text-gray-600 hover:border-gray-300">
            ⤓ יצוא לאקסל
          </button>
          <button onClick={() => setEditingBonus(true)}
            className="inline-flex items-center gap-1 text-sm rounded-lg px-4 py-1.5 border border-gray-200 text-gray-600 hover:border-gray-300">
            ⚙ מדיניות בונוס
          </button>
          <button onClick={() => setAddingNew(true)}
            className="inline-flex items-center gap-1 text-sm rounded-lg px-4 py-1.5 border border-blue-200 text-blue-700 hover:border-blue-300 font-medium">
            <span className="text-base leading-none">+</span> הוסף עובד
          </button>
        </div>
      </div>

      {viewingSnap && (
        <div className="flex items-center gap-3 mb-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800">
          📸 מציג snapshot מ-{viewingSnap} (לקריאה בלבד)
          <button onClick={() => load(month)} className="mr-auto text-amber-700 underline">חזרה לחי</button>
        </div>
      )}

      {/* The queue of unset billing profiles. Without this banner these rows show a
          net computed from assumed defaults, indistinguishable from a real one —
          17 therapists (₪117K gross of July) sat like that unmarked. Each name
          opens the same editor the ✎ column does. */}
      {!viewingSnap && missingProfiles.length > 0 && (
        <div className="mb-3 rounded-lg bg-orange-50 border border-orange-300 px-4 py-3 text-sm">
          <div className="text-orange-900 font-medium">
            ⚠ {missingProfiles.length} מטפלים ללא פרופיל חיוב · {ils(missingGross)} ברוטו —
            הנטו שלהם מוצג לפי ברירת מחדל (עצמאי · מורשה · 0% ניכוי) ואינו סופי
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {missingProfiles.map((t) => (
              <button key={t.name} onClick={() => setEditingT(t)}
                title="פתח עריכת פרופיל"
                className="text-xs rounded-full border border-orange-300 bg-white px-2.5 py-1 text-orange-800 hover:bg-orange-100">
                {t.hebrew_name || t.name} · {ils(t.total_payment)}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Two different failures, one consequence: a doctor worked and is being paid
          nothing. `unmatched` have no payroll row at all — their meetings are dropped
          from every total on this screen — and `noTariff` have a row with no numbers
          in it. Both at the top, because a name in a corner gets scrolled past
          (Ohad, 2026-10-06). */}
      {!viewingSnap && (unmatched.length > 0 || noTariff.length > 0) && (
        <div className="mb-3 rounded-lg bg-red-50 border border-red-300 px-4 py-3 text-sm">
          <div className="text-red-900 font-medium">
            🔴 {unmatched.length + noTariff.length} רופאים עם פגישות ובלי תעריף ·{' '}
            {unmatched.reduce((a, u) => a + u.completed_meetings, 0) + noTariffMeetings} פגישות —
            התשלום שלהם מחושב כ-₪0.00
          </div>
          {unmatched.length > 0 && (
            <div className="mt-2">
              <div className="text-xs text-red-800 mb-1">
                אינם ברשימת השכר כלל — הפגישות שלהם לא נספרות באף סכום במסך:
              </div>
              <div className="flex flex-wrap gap-1.5">
                {unmatched.map((u) => (
                  <button key={u.name}
                    onClick={() => { setAddingNewName(u.name); setAddingNewType(u.suggested_type); }}
                    title={`הוספה לרשימת השכר · לפי היומן: ${
                      u.suggested_type === 'Psychiatrist' ? 'פסיכיאטר' : 'מנהל תיק'} (${
                      u.psy_meetings} פסיכיאטר, ${u.cm_meetings} מנהל תיק)`}
                    className="text-xs rounded-full border border-red-400 bg-white px-2.5 py-1 text-red-900 hover:bg-red-100">
                    {u.name} · {u.completed_meetings} פגישות
                    {u.suggested_type && (
                      <span className="text-red-500">
                        {' '}· {u.suggested_type === 'Psychiatrist' ? 'פסיכיאטר' : 'מנהל תיק'}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
          {noTariff.length > 0 && (
            <div className="mt-2">
              <div className="text-xs text-red-800 mb-1">ברשימת השכר, אך ללא תעריף:</div>
              <div className="flex flex-wrap gap-1.5">
                {noTariff.map((t) => (
                  <button key={t.name} onClick={() => setEditingT(t)}
                    title="פתח עריכת תעריפים"
                    className="text-xs rounded-full border border-red-300 bg-white px-2.5 py-1 text-red-800 hover:bg-red-100">
                    {t.hebrew_name || t.name} · {t.completed_meetings} פגישות
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div className="text-sm text-gray-500">טוען…</div>
      ) : (
        <div className={refreshing ? 'opacity-60 transition-opacity' : ''}>
        {/* max-h + internal scroll so the sticky header actually has a scroll
            container — Lee's ask (6.8): column titles visible at the bottom rows too */}
        <div className="overflow-auto rounded-lg border border-gray-200 max-h-[75vh]">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs sticky top-0 z-10 shadow-sm">
              <tr>
                <th className="px-3 py-2 text-center">עריכה</th>
                <th className="px-3 py-2 text-right">שם מלא</th>
                <th className="px-3 py-2 text-right">תפקיד</th>
                <th className="px-3 py-2 text-left">תשלום ללא מע״מ</th>
                <th className="px-3 py-2 text-left">כולל מע״מ</th>
                <th className="px-3 py-2 text-left">ניכוי מס במקור</th>
                <th className="px-3 py-2 text-left">נטו לתשלום</th>
                {/* The stage columns filter right from their headers (Ohad, 6.8) —
                    same state as the filter bar, so the two can never disagree. */}
                {/* Lee's question before this column existed was "has he even sent
                    it yet" — and nothing on the screen answered it. */}
                <th className="px-1 py-2 text-center">חשבונית נשלחה</th>
                <th className="px-1 py-2 text-center">
                  <select value={fInvoice} onChange={(e) => setFInvoice(e.target.value as any)}
                    className={`bg-transparent text-xs font-medium cursor-pointer ${fInvoice !== 'all' ? 'text-blue-700' : ''}`}>
                    <option value="all">מס׳ חשבונית ▾</option>
                    <option value="set">חשבונית: הוזן</option>
                    <option value="empty">חשבונית: חסר</option>
                  </select>
                </th>
                <th className="px-1 py-2 text-center">
                  <select value={fBasel} onChange={(e) => setFBasel(e.target.value as any)}
                    className={`bg-transparent text-xs font-medium cursor-pointer ${fBasel !== 'all' ? 'text-blue-700' : ''}`}>
                    <option value="all">באסל ▾</option>
                    <option value="yes">באסל: אישר</option>
                    <option value="no">באסל: טרם</option>
                  </select>
                </th>
                <th className="px-1 py-2 text-center">
                  <select value={fTal} onChange={(e) => setFTal(e.target.value as any)}
                    className={`bg-transparent text-xs font-medium cursor-pointer ${fTal !== 'all' ? 'text-blue-700' : ''}`}>
                    <option value="all">לילך ▾</option>
                    <option value="yes">לילך: אישרה</option>
                    <option value="no">לילך: טרם</option>
                  </select>
                </th>
                <th className="px-1 py-2 text-center">
                  <select value={fLee} onChange={(e) => setFLee(e.target.value as any)}
                    className={`bg-transparent text-xs font-medium cursor-pointer ${fLee !== 'all' ? 'text-blue-700' : ''}`}>
                    <option value="all">לי ▾</option>
                    <option value="yes">לי: אישרה</option>
                    <option value="no">לי: טרם</option>
                  </select>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((t) => {
                const done = tal[t.name] && lee[t.name];
                return (
                  <tr key={t.name} className={done ? 'bg-green-50/50 text-gray-500' : ''}>
                    <td className="px-3 py-2 text-center">
                      <button onClick={() => setEditingT(t)} title="עריכת תעריפים ופרופיל"
                        className="text-gray-500 hover:text-blue-600">✎</button>
                    </td>
                    <td className="px-3 py-2 font-medium text-gray-800">
                      <button onClick={() => setDetailT(t)} title="פירוט העבודה" className="hover:text-blue-700 text-right">
                        {t.hebrew_name || t.name}
                      </button>
                      {/* The English name is the calendar key — it is what the meetings
                          attach to. Hiding it behind the Hebrew display name left Basel
                          unable to check that the two still match, which is exactly how
                          ten doctors dropped out of payroll when a "Dr" prefix was added
                          upstream (Ohad, 2026-10-06). */}
                      {t.hebrew_name && t.hebrew_name !== t.name && (
                        <div dir="ltr" className="text-[11px] font-normal text-gray-500 text-right">{t.name}</div>
                      )}
                      {t.demo && <span className="mr-1.5 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">דמה</span>}
                      <button
                        onClick={(e) => { e.stopPropagation(); openHistory(t.name); }}
                        title="כל החודשים של המטפל הזה"
                        className="mr-1.5 text-gray-500 hover:text-blue-700"
                      >🗓</button>
                    </td>
                    <td className="px-3 py-2 text-gray-600">{t.role === 'psy' ? 'פסיכיאטר' : 'מנהל תיק'}</td>
                    <td className="px-3 py-2 text-left tabular-nums">
                      <CopyAmount value={t.total_payment}>{ils(t.total_payment)}</CopyAmount>
                    </td>
                    <td className="px-3 py-2 text-left tabular-nums">
                      {t.billing.vat_registered
                        ? <CopyAmount value={t.billing.incl_vat}>{ils(t.billing.incl_vat)}</CopyAmount>
                        : '—'}
                    </td>
                    <td className="px-3 py-2 text-left tabular-nums text-gray-500">
                      {t.billing.withholding_rate > 0
                        ? <>{Math.round(t.billing.withholding_rate * 100)}% · <CopyAmount value={t.billing.withholding_amount}>{ils(t.billing.withholding_amount)}</CopyAmount></>
                        : '—'}
                    </td>
                    <td className="px-3 py-2 text-left tabular-nums font-semibold text-gray-900">
                      {t.billing.is_set === false
                        ? <span className="text-orange-600 bg-orange-50 rounded px-1.5 py-0.5"
                            title="אין פרופיל חיוב — נטו לפי ברירת מחדל, אינו סופי">
                            <CopyAmount value={t.billing.net_pay}>{ils(t.billing.net_pay)}</CopyAmount> ⚠
                          </span>
                        : <CopyAmount value={t.billing.net_pay}>{ils(t.billing.net_pay)}</CopyAmount>}
                    </td>
                    <td className="px-1 py-2 text-center">
                      {invSent[t.name] !== undefined ? (
                        <span className="text-xs text-green-700 whitespace-nowrap"
                          title={`המטפל/ת סימן/ה ששלח/ה חשבונית${invSent[t.name]
                            ? ` ב-${new Date(invSent[t.name]).toLocaleString('he-IL')}` : ''}`}>
                          ✓ {invSent[t.name]
                            ? new Date(invSent[t.name]).toLocaleDateString('he-IL')
                            : ''}
                        </span>
                      ) : <span className="text-xs text-gray-500">—</span>}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <div className="flex items-center justify-center gap-1.5">
                        <input value={inv[t.name] || ''}
                          onChange={(e) => setInv({ ...inv, [t.name]: e.target.value })}
                          onBlur={(e) => persist(t.name, 'invoice', e.target.value)}
                          placeholder="—" className="w-24 text-xs border border-gray-200 rounded px-1.5 py-1 text-center" />
                        <InvoiceTypeToggle t={t} month={month} onFlip={(v) => setInvoiceType(t.name, v)} />
                      </div>
                    </td>
                    {/* Basel's gate: shown, never set from here. Tal and Lee wait on
                        it — approving an invoice before payroll signed the figure is
                        the mistake the order exists to prevent. */}
                    <td className="px-3 py-2 text-center">
                      {basel[t.name]
                        ? <span className="text-xs font-medium text-green-700 whitespace-nowrap">✓ אושר</span>
                        : <span className="text-xs text-gray-500 whitespace-nowrap" title="ממתין לאישור באסל במסך התשלומים">ממתין</span>}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <input type="checkbox" checked={!!tal[t.name]} disabled={!basel[t.name]}
                        title={basel[t.name] ? '' : 'ממתין לאישור באסל'}
                        onChange={(e) => { setTal({ ...tal, [t.name]: e.target.checked }); persist(t.name, 'tal', e.target.checked ? '1' : '0'); }} />
                    </td>
                    <td className="px-3 py-2 text-center">
                      <input type="checkbox" checked={!!lee[t.name]} disabled={!basel[t.name]}
                        title={basel[t.name] ? '' : 'ממתין לאישור באסל'}
                        onChange={(e) => { setLee({ ...lee, [t.name]: e.target.checked }); persist(t.name, 'lee', e.target.checked ? '1' : '0'); }} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="text-sm border-t-2 border-gray-200">
              {/* 11 columns now (a באסל column was added 2026-07-30): the leading
                  labels cover עריכה+שם+תפקיד = 3, the trailing spacers cover
                  חשבונית+באסל+לילך+לי = 4. */}
              <tr className="bg-gray-50 font-semibold text-gray-800">
                <td className="px-3 py-2.5" colSpan={3}>סה״כ · {rows.length} מטפלים</td>
                <td className="px-3 py-2.5 text-left tabular-nums"><CopyAmount value={totals.before_vat}>{ils(totals.before_vat)}</CopyAmount></td>
                <td className="px-3 py-2.5 text-left tabular-nums"><CopyAmount value={totals.incl_vat}>{ils(totals.incl_vat)}</CopyAmount></td>
                <td className="px-3 py-2.5 text-left tabular-nums text-gray-500">
                  {totals.withholding ? <CopyAmount value={totals.withholding}>{ils(totals.withholding)}</CopyAmount> : '—'}
                </td>
                <td className="px-3 py-2.5 text-left tabular-nums text-gray-900"><CopyAmount value={totals.net}>{ils(totals.net)}</CopyAmount></td>
                <td colSpan={4} />
              </tr>
              <tr className="bg-emerald-50/60 text-emerald-900">
                <td className="px-3 py-2.5" colSpan={3}>
                  שולם
                  <span className="mr-2 text-xs font-normal text-emerald-700">
                    {totals.paidCount} עם שני האישורים
                  </span>
                </td>
                <td colSpan={3} />
                <td className="px-3 py-2.5 text-left tabular-nums font-semibold"><CopyAmount value={totals.paid}>{ils(totals.paid)}</CopyAmount></td>
                <td colSpan={4} />
              </tr>
              <tr className="bg-amber-50/60 text-amber-900">
                <td className="px-3 py-2.5" colSpan={3}>
                  נותר לתשלום
                  <span className="mr-2 text-xs font-normal text-amber-700">
                    {totals.pending} ללא שני האישורים
                  </span>
                </td>
                <td colSpan={3} />
                <td className="px-3 py-2.5 text-left tabular-nums font-semibold"><CopyAmount value={totals.remaining}>{ils(totals.remaining)}</CopyAmount></td>
                <td colSpan={4} />
              </tr>
            </tfoot>
          </table>
        </div>
        </div>
      )}
      <div className="mt-2 text-xs text-gray-500">
        העמודות המחושבות מהמנוע; חשבונית / סוג חשבונית / אישור לילך / אישור לי — נשמרים אוטומטית לכל שינוי. אישור באסל נקבע במסך התשלומים ומוצג כאן בלבד.
      </div>

      {deleted.length > 0 && (
        <div className="mt-1 text-xs text-gray-500">
          <button onClick={() => setShowDeleted(!showDeleted)} className="hover:text-gray-600">
            מחוקים ({deleted.length}) {showDeleted ? '▲' : '▼'}
          </button>
          {showDeleted && (
            <div className="mt-1 flex flex-wrap gap-2">
              {deleted.map((n) => (
                <span key={n} className="inline-flex items-center gap-2 rounded border border-gray-200 px-2 py-1">
                  <span className="text-gray-500">{n}</span>
                  <button
                    onClick={async () => {
                      await execApi.setRosterState({ therapist: n, state: 'active', month });
                      load(month);
                    }}
                    className="text-blue-600 hover:underline">שחזור</button>
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {editingT && (
        <TherapistCompEditor therapist={editingT} month={month}
          onClose={() => setEditingT(null)} onSaved={() => load(month)} />
      )}
      {detailT && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" dir="rtl">
          <div className="absolute inset-0 bg-black/30" onClick={() => setDetailT(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-800">{detailT.hebrew_name || detailT.name} · פירוט עבודה</h2>
              <button onClick={() => setDetailT(null)} className="text-gray-500 hover:text-gray-700 text-xl">✕</button>
            </div>
            <TherapistDetail therapist={detailT as unknown as DetailTherapist}
              month={month} isCurrent={month === thisMonth()} />
          </div>
        </div>
      )}
      {/* Lee's view: one therapist, every month. "Paid" is the same rule the month
          screen uses — tal AND lee together, never one tick alone. */}
      {histFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" dir="rtl">
          <div className="absolute inset-0 bg-black/30" onClick={() => setHistFor(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-[92vw] xl:max-w-6xl p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-800">
                {hist?.hebrew_name || histFor} · כל החודשים
              </h2>
              <button onClick={() => setHistFor(null)} className="text-gray-500 hover:text-gray-700 text-xl">✕</button>
            </div>

            {histLoading && <div className="py-10 text-center text-gray-500">טוען את כל החודשים…</div>}
            {!histLoading && !hist && <div className="py-10 text-center text-gray-500">לא נמצאו נתונים</div>}

            {!histLoading && hist && (
              <>
                <div className="mb-4 flex gap-4 text-sm">
                  <div className="rounded-lg border border-gray-200 px-4 py-2">
                    <div className="text-xs text-gray-500">סה״כ נטו</div>
                    <div className="text-lg font-bold tabular-nums"><CopyAmount value={hist.total_net}>{ils(hist.total_net)}</CopyAmount></div>
                  </div>
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2">
                    <div className="text-xs text-emerald-700">מזה שולם</div>
                    <div className="text-lg font-bold tabular-nums text-emerald-700"><CopyAmount value={hist.paid_net}>{ils(hist.paid_net)}</CopyAmount></div>
                  </div>
                </div>
                <div className="overflow-x-auto">
                <table className="w-full text-sm whitespace-nowrap">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className="px-3 py-2 text-right">חודש</th>
                      <th className="px-3 py-2 text-right">פגישות</th>
                      <th className="px-3 py-2 text-left">תשלום ללא מע״מ</th>
                      <th className="px-3 py-2 text-left">כולל מע״מ</th>
                      <th className="px-3 py-2 text-left">ניכוי מס במקור</th>
                      <th className="px-3 py-2 text-left">נטו לתשלום</th>
                      <th className="px-3 py-2 text-center">באסל</th>
                      <th className="px-3 py-2 text-center">לילך</th>
                      <th className="px-3 py-2 text-center">לי</th>
                      <th className="px-3 py-2 text-center">שולם</th>
                      <th className="px-3 py-2 text-right">חשבונית</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hist.months.map((m: any) => {
                      const paid = m.tal && m.lee;
                      return (
                        <tr key={m.month} className="border-t border-gray-100">
                          <td className="px-3 py-2 font-medium">{m.month}</td>
                          <td className="px-3 py-2 tabular-nums text-gray-600">{m.completed_meetings || '—'}</td>
                          <td className="px-3 py-2 text-left tabular-nums"><CopyAmount value={m.total_payment}>{ils(m.total_payment)}</CopyAmount></td>
                          <td className="px-3 py-2 text-left tabular-nums">
                            {m.vat_registered ? <CopyAmount value={m.incl_vat}>{ils(m.incl_vat)}</CopyAmount> : '—'}
                          </td>
                          <td className="px-3 py-2 text-left tabular-nums text-gray-500">
                            {m.withholding ? <CopyAmount value={m.withholding}>{'−' + ils(m.withholding)}</CopyAmount> : '—'}
                          </td>
                          <td className="px-3 py-2 text-left tabular-nums font-medium"><CopyAmount value={m.net_pay}>{ils(m.net_pay)}</CopyAmount></td>
                          {/* Basel's sign-off: shown, never set from here — the server
                              refuses the field from this screen anyway, so a box here was
                              a click that could only ever fail. Same read-only treatment
                              as the month table. */}
                          <td className="px-3 py-2 text-center">
                            {m.basel
                              ? <span className="text-xs font-medium text-green-700 whitespace-nowrap">✓ אושר</span>
                              : <span className="text-xs text-gray-500 whitespace-nowrap" title="ממתין לאישור באסל במסך התשלומים">ממתין</span>}
                          </td>
                          {/* The chain (Ohad, 2026-09-14): Basel approves the figure →
                              the therapist sends an invoice for it → only then do Tal and
                              Lee approve and pay. The month table locks these two until
                              Basel signed; this window did not, and the server accepts
                              them, so it was a way around the gate. */}
                          {(['tal', 'lee'] as const).map((f) => (
                            <td key={f} className="px-3 py-2 text-center">
                              {/* A box, not a character: the ✓/· target was a few pixels
                                  and you had to hunt for it with the cursor. */}
                              <button
                                onClick={() => tickHistory(histFor!, m.month, f, !m[f])}
                                disabled={!m.basel}
                                title={!m.basel ? 'ממתין לאישור באסל' : m[f] ? 'בטל סימון' : 'סמן'}
                                className={`inline-flex h-6 w-6 items-center justify-center rounded border text-sm transition-colors ${
                                  !m.basel
                                    ? 'border-gray-200 bg-gray-50 text-transparent cursor-not-allowed'
                                    : m[f]
                                      ? 'border-emerald-500 bg-emerald-500 text-white'
                                      : 'border-gray-300 bg-white text-transparent hover:border-emerald-400 hover:text-emerald-300'
                                }`}
                              >✓</button>
                            </td>
                          ))}
                          <td className={`px-3 py-2 text-center font-semibold ${paid ? 'text-emerald-600' : 'text-gray-500'}`}>
                            {paid ? '✓' : '·'}
                          </td>
                          <td className="px-3 py-2 text-gray-600">{m.invoice || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {(addingNew || addingNewName) && (
        <TherapistCompEditor isNew month={month}
          suggestedType={addingNewName ? addingNewType : undefined}
          therapist={{
            name: addingNewName || '', hebrew_name: '', archived: false,
            rates: {
              workedRate: 0, nonClinicalRate: 0, oncallRate: 1500,
              tariffIntakeAdult: 0, tariffIntakeYouth: 0,
              tariffFollowupAdult: 0, tariffFollowupYouth: 0,
              intakeLengthAdults: 0, intakeLengthYouth: 0,
              followupLengthAdults: 0, followupLengthYouth: 0,
              noshowPct: 0, ...NEW_BONUS,
            },
            billing: { employment: 'freelancer', vat_registered: true, withholding_rate: 0 },
          }}
          onClose={() => { setAddingNew(false); setAddingNewName(null); }}
          onSaved={() => load(month)} />
      )}
      {editingBonus && (
        <BonusPolicyEditor month={month}
          onClose={() => setEditingBonus(false)} onSaved={() => load(month)} />
      )}
    </div>
  );
}
